import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/cn'

type ButtonVariant = 'primary' | 'secondary' | 'soft' | 'dangerSoft' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md'

const variantClassName: Record<ButtonVariant, string> = {
  primary: 'bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]',
  secondary:
    'border border-[var(--color-border)] bg-white text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]',
  soft: 'border border-transparent bg-[var(--color-primary-soft)] text-[var(--color-primary)] hover:bg-[rgba(25,91,255,0.2)]',
  dangerSoft:
    'border border-transparent bg-[rgba(217,45,32,0.1)] text-[var(--color-danger)] hover:bg-[rgba(217,45,32,0.16)]',
  ghost: 'bg-transparent text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]',
  danger: 'bg-[var(--color-danger)] text-white hover:bg-[var(--color-danger-hover)]',
}

const sizeClassName: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  isLoading?: boolean
  size?: ButtonSize
  variant?: ButtonVariant
}

export function Button({
  children,
  className,
  disabled,
  isLoading = false,
  size = 'md',
  type = 'button',
  variant = 'primary',
  ...props
}: ButtonProps) {
  const isDisabled = Boolean(disabled) || isLoading

  return (
    <button
      className={cn(
        'inline-flex cursor-pointer items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variantClassName[variant],
        sizeClassName[size],
        className,
      )}
      disabled={isDisabled}
      type={type}
      {...props}
    >
      {isLoading ? (
        <span aria-hidden className="size-3 shrink-0 animate-pulse rounded-full bg-current" />
      ) : null}
      {children}
    </button>
  )
}
